import React, { useMemo } from 'react';
import { useField } from 'formik';
import { Select as AntdSelect, Avatar, TreeSelect } from 'antd';
import { DownOutlined, WarningOutlined } from '@ant-design/icons';
import cx from 'classnames';

import styles from '../Input/Input.module.css';
import myStyles from './Select.module.css';
import { useTranslation } from 'react-i18next';

const Select = ({
  label = "",
  name = "",
  placeholder = "",
  size = "middle",
  className = "",
  mode = "",
  required = false,
  disabled = false,
  options = [],
  tagRender = null,
  suffixIcon = <DownOutlined />,
  onChange = () => { },
  onSearch = () => { },
  notFoundContent = null,
  tree = false,
  multiple = false,
  sorting = true,
}) => {
  const { i18n } = useTranslation();
  const [field, meta, helpers] = useField(name);
  const { onBlur } = field;
  const { value, error, touched } = meta;
  const { setValue } = helpers;


  const buildTreeDataRecursive = (data, parentCode = null, seen = new Set()) => {
    return data
      .filter(item => (item.under ? item.under.id : null) === parentCode)
      .map(item => {
        if (seen.has(item.id)) {
          return null;
        }

        seen.add(item.id);

        const children = buildTreeDataRecursive(data, item.id, seen);

        return {
          title: i18n.language == 'th' ? item.name : item.nameEn,
          value: item.id,
          children: children.length > 0 ? children : undefined,
        };
      })
      .filter(item => item !== null);
  };

  const handleChange = (val, rest) => {
    setValue(val);
    onChange(val, rest);
  };

  const translatedError =
    touched && error
      ? i18n?.language === "th"
        ? `กรุณาเลือก ${label}`
        : `Please enter ${label?.toLowerCase?.()}`
      : "";

  const processedOptions = useMemo(() => {
    const sortedOptions = [...options]
      ?.map(option => ({
        ...option,
        originalLabel: option.label,
        label: (
          <div className={myStyles.option}>
            {option.imageData && (
              <Avatar style={{ marginRight: "5px" }} size={24} src={`data:image/png;base64,${option?.imageData}`} alt={option.label} />
            )}
            {option.icon && (
              <Avatar style={{ marginRight: "5px" }} size={24} src={option?.icon} alt={option.label} />
            )}
            {option.symbol && !option.icon && (
              <Avatar style={{ marginRight: '5px', backgroundColor: "#000000" }} size={24} gap={0}>
                {option.symbol}
              </Avatar>
            )}
            <span>{option.label}</span>
          </div>
        )
      }))

    if (sorting) {
      return sortedOptions?.sort((a, b) =>
        a?.originalLabel?.localeCompare?.(b?.originalLabel, i18n.language === "en" ? "en" : "th", {
          sensitivity: "base",
        })
      );
    }

    return sortedOptions;
  }, [options, i18n.language]);

  const filterOption = (input, option) =>
    option.originalLabel.toLowerCase().includes(input.toLowerCase());

  const sortedTreeOptions = useMemo(() => {
    const sorted = [...options].sort((a, b) =>
      (i18n.language === "en" ? a.nameEn : a.name)?.localeCompare?.(
        i18n.language === "en" ? b.nameEn : b.name,
        i18n.language === "en" ? "en" : "th",
        { sensitivity: "base" }
      )
    );
    return buildTreeDataRecursive(sorted);
  }, [options, i18n.language]);

  return (
    <div className={cx(styles.container, className)}>
      {label && (
        <label className={cx("label", styles.label)} htmlFor={name}>
          {label}
          {required && <span className="error">*</span>}
        </label>
      )}
      {tree ? (
        <TreeSelect
          treeData={sortedTreeOptions}
          placeholder={placeholder || label}
          onChange={handleChange}
          treeDefaultExpandAll
          allowClear
          className={myStyles.input}
          disabled={disabled}
          size={size}
          multiple={multiple}
          value={value ?? []}
          onBlur={onBlur}
        />
      ) : (
        <AntdSelect
          allowClear
          showSearch
          popupMatchSelectWidth={false}
          required={required}
          filterOption={filterOption}
          id={name}
          className={myStyles.input}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onSearch={onSearch}
          onBlur={onBlur}
          data-state={touched && error ? "error" : ""}
          status={touched && error ? "error" : ""}
          disabled={disabled}
          mode={mode}
          tagRender={tagRender}
          size={size}
          options={processedOptions}
        // suffixIcon={(e) => {
        //   return e
        // }}
        />
      )}

      {translatedError && (
        <span className={cx("error", styles.error)}>
          <WarningOutlined /> {translatedError}
        </span>
      )}
    </div>
  );
};

export default Select;
